import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { zoneLabel } from "@/lib/zones";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Copy, RefreshCw, Trash2, Users, Siren, ChevronRight, UserMinus, ArrowLeftRight } from "lucide-react";
import { ClubReport } from "@/components/ClubReport";
import { ClubNotifications } from "@/components/ClubNotifications";

const L = {
  en: { title: "Club Dashboard", subtitle: "Create teams, share the join code with your players and follow their readiness, pain alerts and injuries in real time.",
    newTeam: "New team", name: "Team name", namePh: "e.g. U17 A, Seniors", category: "Category", catPh: "U15, U17, Seniors…", create: "Create team",
    teams: "Teams", code: "Join code", copy: "Copy", copied: "Code copied", regen: "New code", players: "players", noTeams: "No team yet — create your first one.",
    roster: "Roster", compare: "Compare", noMembers: "No player yet. Share the code", player: "Player", readiness: "Readiness", risk: "Injury risk", alerts: "Alerts", lastLog: "Last log", sleep: "Sleep", injuries: "Injuries", speed: "Speed", remove: "Remove", view: "View", private: "private", never: "never" },
  fr: { title: "Espace Club", subtitle: "Crée des équipes, partage le code d'invitation à tes joueurs et suis leur fraîcheur, alertes douleur et blessures en temps réel.",
    newTeam: "Nouvelle équipe", name: "Nom de l'équipe", namePh: "ex : U17 A, Seniors", category: "Catégorie", catPh: "U15, U17, Seniors…", create: "Créer l'équipe",
    teams: "Équipes", code: "Code d'invitation", copy: "Copier", copied: "Code copié", regen: "Nouveau code", players: "joueurs", noTeams: "Aucune équipe — crée la première.",
    roster: "Effectif", compare: "Comparer", noMembers: "Aucun joueur. Partage le code", player: "Joueur", readiness: "Fraîcheur", risk: "Risque bless.", alerts: "Alertes", lastLog: "Dernier suivi", sleep: "Sommeil", injuries: "Blessures", speed: "Vitesse", remove: "Retirer", view: "Voir", private: "privé", never: "jamais" },
};

const scoreCls = (v, inverse = false) => {
  if (v == null) return "text-slate-500";
  const good = inverse ? v < 40 : v >= 70, bad = inverse ? v >= 70 : v < 40;
  return good ? "text-[#00F076]" : bad ? "text-[#FF2E54]" : "text-amber-400";
};

const ago = (iso, lang) => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  return d === 0 ? (lang === "fr" ? "auj." : "today") : `${d}j`;
};

export default function Club() {
  const { lang } = useLang();
  const t = L[lang] || L.en;
  const { user } = useAuth();
  const nav = useNavigate();
  const [teams, setTeams] = useState([]);
  const [form, setForm] = useState({ name: "", category: "" });
  const [selected, setSelected] = useState(null);
  const [roster, setRoster] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadTeams = async () => {
    const r = await api.get("/club/teams"); setTeams(r.data);
    if (!selected && r.data[0]) setSelected(r.data[0].id);
    return r.data;
  };
  useEffect(() => { loadTeams(); }, []);
  useEffect(() => { if (selected) api.get(`/club/teams/${selected}/members`).then((r) => setRoster(r.data)); else setRoster(null); }, [selected]);

  const create = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try { const r = await api.post("/club/teams", form); setForm({ name: "", category: "" }); await loadTeams(); setSelected(r.data.id); toast.success(r.data.code); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); } finally { setBusy(false); }
  };
  const del = async (id) => { await api.delete(`/club/teams/${id}`); if (selected === id) setSelected(null); const list = await loadTeams(); if (list[0] && selected === id) setSelected(list[0].id); };
  const regen = async (id) => { await api.post(`/club/teams/${id}/regenerate-code`); loadTeams(); };
  const copy = (code) => { navigator.clipboard?.writeText(code); toast.success(t.copied); };
  const removeMember = async (uid) => { await api.delete(`/club/teams/${selected}/members/${uid}`); const r = await api.get(`/club/teams/${selected}/members`); setRoster(r.data); loadTeams(); };

  const team = teams.find((x) => x.id === selected);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="club-page">
      <div className="text-[10px] font-mono-tech tracking-[0.3em] text-emerald-400">{user?.club_name?.toUpperCase()} {user?.city && `· ${user.city.toUpperCase()}`}</div>
      <h1 className="font-display font-black uppercase text-4xl sm:text-5xl mt-2" data-testid="club-title">{t.title}</h1>
      <p className="text-slate-400 mt-2 max-w-2xl">{t.subtitle}</p>

      <div className="mt-8 grid lg:grid-cols-4 gap-6">
        <div className="space-y-4">
          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5 space-y-3">
            <div className="font-display font-bold uppercase text-base flex items-center gap-2"><Plus className="w-4 h-4 text-[#00F076]" />{t.newTeam}</div>
            <div>
              <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">{t.name}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t.namePh} data-testid="club-team-name-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
            </div>
            <div>
              <Label className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">{t.category}</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder={t.catPh} data-testid="club-team-category-input" className="mt-1 bg-[#090C10] border-[#1E2638] text-white" />
            </div>
            <Button onClick={create} disabled={busy || !form.name.trim()} data-testid="club-team-create-btn" className="w-full bg-[#00F076] hover:bg-[#00C963] text-black font-bold uppercase text-xs">{t.create}</Button>
          </div>

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5">
            <div className="font-display font-bold uppercase text-base mb-3">{t.teams}</div>
            {teams.length === 0 && <div className="text-slate-500 text-xs" data-testid="club-teams-empty">{t.noTeams}</div>}
            <div className="space-y-2">
              {teams.map((tm) => (
                <button key={tm.id} onClick={() => setSelected(tm.id)} data-testid={`club-team-${tm.id}`}
                  className={`w-full text-left rounded-lg border p-3 transition-colors ${selected === tm.id ? "border-[#00F076] bg-[#00F076]/5" : "border-[#1E2638] bg-[#090C10] hover:border-[#00F076]/40"}`}>
                  <div className="flex justify-between items-center">
                    <div className="font-display font-bold text-sm">{tm.name}</div>
                    <div className="text-[10px] font-mono-tech text-slate-400 flex items-center gap-1"><Users className="w-3 h-3" />{tm.member_count}</div>
                  </div>
                  {tm.category && <div className="text-[10px] font-mono-tech uppercase text-slate-500">{tm.category}</div>}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-3 space-y-4">
          {team && (
            <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5 flex flex-wrap items-center gap-4" data-testid="club-team-header">
              <div className="flex-1 min-w-[160px]">
                <div className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400">{t.code}</div>
                <div className="font-display font-black text-3xl tracking-[0.2em] text-[#00F076]" data-testid="club-team-code">{team.code}</div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" onClick={() => copy(team.code)} data-testid="club-code-copy-btn" className="bg-[#00F076] hover:bg-[#00C963] text-black font-bold text-xs uppercase"><Copy className="w-3 h-3 mr-1" />{t.copy}</Button>
                <Button size="sm" variant="outline" onClick={() => regen(team.id)} data-testid="club-code-regen-btn" className="border-[#1E2638] text-slate-300 text-xs uppercase"><RefreshCw className="w-3 h-3 mr-1" />{t.regen}</Button>
                <Button size="sm" variant="ghost" onClick={() => del(team.id)} data-testid="club-team-delete-btn" className="text-slate-400 hover:text-red-400"><Trash2 className="w-4 h-4" /></Button>
              </div>
            </div>
          )}

          <ClubNotifications />

          {team && <ClubReport teamId={team.id} />}

          <div className="rounded-xl border border-[#1E2638] bg-[#121721] p-5" data-testid="club-roster">
            <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
              <div className="font-display font-bold uppercase text-lg">{t.roster} {roster && <span className="text-slate-500 text-sm">· {roster.members.length} {t.players}</span>}</div>
              {roster?.members.length >= 2 && <Button size="sm" variant="outline" onClick={() => nav(`/club/compare?a=${roster.members[0].id}&b=${roster.members[1].id}`)} data-testid="club-compare-btn" className="border-[#1E2638] text-slate-300 text-xs uppercase"><ArrowLeftRight className="w-3 h-3 mr-1" />{t.compare}</Button>}
            </div>
            {!team && <div className="text-slate-500 text-sm">{t.noTeams}</div>}
            {team && roster?.members.length === 0 && <div className="text-slate-500 text-sm" data-testid="club-roster-empty">{t.noMembers} <b className="text-[#00F076] font-mono-tech">{team.code}</b></div>}
            {roster?.members.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm" data-testid="club-roster-table">
                  <thead>
                    <tr className="text-[10px] font-mono-tech uppercase tracking-wider text-slate-400 text-left">
                      <th className="py-2 pr-3">{t.player}</th><th className="py-2 px-2 text-center">{t.readiness}</th><th className="py-2 px-2 text-center">{t.risk}</th>
                      <th className="py-2 px-2">{t.alerts}</th><th className="py-2 px-2 text-center">{t.lastLog}</th><th className="py-2 px-2 text-center">{t.sleep}</th>
                      <th className="py-2 px-2 text-center">{t.injuries}</th><th className="py-2 px-2 text-center">{t.speed}</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {roster.members.map((m) => (
                      <tr key={m.id} className="border-t border-[#1E2638] hover:bg-[#090C10] cursor-pointer" onClick={() => nav(`/club/players/${m.id}`)} data-testid={`club-member-${m.id}`}>
                        <td className="py-3 pr-3">
                          <div className="font-display font-bold">{m.full_name}</div>
                          <div className="text-[10px] font-mono-tech uppercase text-slate-500">{m.position}</div>
                        </td>
                        <td className={`px-2 text-center font-display font-black text-xl ${scoreCls(m.scores?.readiness)}`}>{m.scores?.readiness ?? "—"}</td>
                        <td className={`px-2 text-center font-display font-black text-xl ${scoreCls(m.scores?.injury_risk, true)}`}>{m.scores?.injury_risk ?? "—"}</td>
                        <td className="px-2">
                          {m.alerts.length === 0 ? <span className="text-slate-600">—</span> : (
                            <div className="flex flex-wrap gap-1">
                              {m.alerts.map((a) => <span key={a.zone} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-[#FF2E54]/20 text-[#FF2E54] text-[10px] font-mono-tech" data-testid={`club-member-alert-${m.id}-${a.zone}`}><Siren className="w-3 h-3" />{zoneLabel(a.zone, lang)} ×{a.streak}</span>)}
                            </div>
                          )}
                        </td>
                        <td className="px-2 text-center font-mono-tech text-xs text-slate-300">{m.latest_daily ? ago(m.latest_daily.created_at, lang) : <span className="text-slate-600">{m.privacy.share_daily ? t.never : t.private}</span>}</td>
                        <td className="px-2 text-center font-mono-tech text-xs">{m.privacy.share_daily && m.latest_daily?.sleep_hours != null ? `${m.latest_daily.sleep_hours}h` : "—"}</td>
                        <td className="px-2 text-center font-mono-tech text-xs">{m.privacy.share_medical ? <span className={m.active_injuries > 0 ? "text-[#FF2E54] font-bold" : "text-slate-300"}>{m.active_injuries}</span> : <span className="text-slate-600">{t.private}</span>}</td>
                        <td className="px-2 text-center font-mono-tech text-xs">{m.latest_stat?.top_speed_kmh != null ? `${m.latest_stat.top_speed_kmh}` : "—"}</td>
                        <td className="pl-2 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                          <Button size="sm" variant="ghost" onClick={() => nav(`/club/players/${m.id}`)} data-testid={`club-member-view-${m.id}`} className="text-[#00F076] text-xs h-7 px-2">{t.view}<ChevronRight className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => removeMember(m.id)} data-testid={`club-member-remove-${m.id}`} className="text-slate-500 hover:text-red-400 h-7 px-2" title={t.remove}><UserMinus className="w-3 h-3" /></Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
